import React, { useEffect, useMemo, useRef, useState } from "react";
import { findMatches, upsertProfile, removeProfileByName } from "../services/profileStore";

const DEBOUNCE_MS = 120;

const NameAutocomplete = ({
  value,
  onChange,
  onSelectProfile,
  placeholder = "Votre prénom",
}) => {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const suggestions = useMemo(() => {
    if (!debounced) return findMatches("");
    return findMatches(debounced);
  }, [debounced]);

  const hasExact = useMemo(() => {
    const q = String(query || "")
      .trim()
      .toLowerCase();
    return suggestions.some(
      (p) =>
        String(p.name || "")
          .trim()
          .toLowerCase() === q,
    );
  }, [suggestions, query]);

  const handleKeyDown = (e) => {
    // Assurer que la liste reste ouverte lors de la navigation clavier
    if ((query || "").trim()) setOpen(true);
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setHighlight((h) => {
          const next = h + 1;
          return next >= suggestions.length ? suggestions.length - 1 : next;
        });
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setHighlight((h) => (h <= 0 ? 0 : h - 1));
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < suggestions.length) {
        const p = suggestions[highlight];
        onSelectProfile && onSelectProfile(p);
        onChange && onChange(p.name);
        setOpen(false);
        setHighlight(-1);
        return;
      }
      // Pas d'élément surligné: valider la saisie courante
      if (query.trim()) {
        onSelectProfile &&
          onSelectProfile({ name: query.trim(), isNew: !hasExact });
        setOpen(false);
        setHighlight(-1);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const selectProfile = (p) => {
    onSelectProfile && onSelectProfile(p);
    onChange && onChange(p.name);
    // Fermer immédiatement les suggestions
    setOpen(false);
    setHighlight(-1);
    // Focus sur l'input après un court délai pour éviter les conflits
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 10);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange && onChange(newValue);

    // Toujours ouvrir les suggestions quand on tape quelque chose
    if (newValue.trim()) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const deleteProfile = (e, profile) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer le profil "${profile.name}" ?\n\nCette action ne peut pas être annulée.`,
      )
    ) {
      try {
        removeProfileByName(profile.name);
        // rafraîchir la liste immédiatement
        const next = findMatches(query);
        setOpen(true);
        setHighlight(-1);
        alert(`Profil "${profile.name}" supprimé.`);
      } catch (err) {
        console.error("Erreur lors de la suppression:", err);
      }
    }
  };

  // Fermer les suggestions si on clique ailleurs, mais ne pas fermer si l'input a encore le focus
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Fermer les suggestions si on clique en dehors de l'autocomplétion
      if (
        !inputRef.current?.contains(e.target) &&
        !listRef.current?.contains(e.target)
      ) {
        // Si l'input a encore le focus, garder la liste ouverte
        if (document.activeElement === inputRef.current && (query || "").trim()) {
          setOpen(true);
          return;
        }
        setOpen(false);
        setHighlight(-1);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [query]);

  return (
    <div className="name-autocomplete" style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if ((query || "").trim()) setOpen(true);
        }}
        onBlur={() => {
          // délai bref pour laisser le temps à un clic sur une suggestion
          setTimeout(() => {
            if (!listRef.current?.contains(document.activeElement)) {
              // ne fermer que si rien n'est sélectionné et pas de focus sur la liste
              setOpen(false);
              setHighlight(-1);
            }
          }, 120);
        }}
        className="name-input"
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <div
          ref={listRef}
          className="name-suggestions"
          style={{
            position: "absolute",
            zIndex: 10,
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 6,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
          }}
        >
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 8,
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            {suggestions.map((p, idx) => (
              <li key={p.id || p.name}>
                <div
                  className="name-suggestion"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    background: highlight === idx ? "#eff6ff" : "#fff",
                    borderRadius: 10,
                    padding: "10px 12px",
                    cursor: "pointer",
                    border: "none",
                  }}
                >
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseLeave={() => setHighlight(-1)}
                    onClick={() => selectProfile(p)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      flex: 1,
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{p.name}</span>
                    {(p.address || p.city || p.postalCode) && (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                        }}
                      >
                        {[
                          p.address,
                          [p.postalCode, p.city].filter(Boolean).join(" "),
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </button>

                  {/* Bouton poubelle */}
                  <button
                    type="button"
                    onClick={(e) => deleteProfile(e, p)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      borderRadius: "4px",
                      color: "#6b7280",
                      fontSize: "16px",
                      marginLeft: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#fef2f2";
                      e.target.style.color = "#dc2626";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.color = "#6b7280";
                    }}
                    title="Supprimer ce profil"
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NameAutocomplete;
